"use client"

import type { MembersHomeOverview } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { useTranslations } from "@/i18n"

type Part = { key: string; label: string; value: number; swatch: string }

/**
 * Members (D4): the branch's head count, its six-month growth and the split by status. Active is green, visitors
 * violet, inactive neutral: the split is also labelled with numbers, so colour is never the only key.
 */
export function MembersTile({
  overview,
  membersLink,
  addMember,
  subtitle,
  emptyMessage,
}: {
  overview: Pick<MembersHomeOverview, "counts" | "growth" | "addedInGrowthWindow">
  /** Null where no members page exists (the parish). */
  membersLink: HomeTileLink | null
  addMember: HomeTileLink | null
  /** Defaults to "in this branch"; the parish board says "across the church". */
  subtitle?: string
  emptyMessage?: string
}) {
  const t = useTranslations("dashboard.home.members")
  const { counts } = overview

  const parts: Part[] = [
    { key: "active", label: t("status.active"), value: counts.active, swatch: "bg-emerald-600 dark:bg-emerald-500" },
    { key: "visitor", label: t("status.visitor"), value: counts.visitor, swatch: "bg-violet-600 dark:bg-violet-400" },
    { key: "inactive", label: t("status.inactive"), value: counts.inactive, swatch: "bg-slate-300 dark:bg-slate-600" },
    ...(counts.other > 0
      ? [{ key: "other", label: t("status.other"), value: counts.other, swatch: "bg-slate-200 dark:bg-slate-700" }]
      : []),
  ]

  return (
    <HomeTile title={t("title")} subtitle={subtitle ?? t("subtitle")} link={membersLink} testId='home-tile-members'>
      {counts.total === 0 ? (
        <HomeTileEmpty message={emptyMessage ?? t("empty")} action={addMember} />
      ) : (
        <>
          <div className='flex items-baseline gap-2'>
            <span className='text-[1.875rem] leading-none font-extrabold tracking-tight tabular-nums'>{counts.total}</span>
            <span className='text-[12.5px] font-bold text-emerald-700 dark:text-emerald-400'>
              {overview.addedInGrowthWindow > 0 ? t("growth", { count: overview.addedInGrowthWindow }) : t("growthNone")}
            </span>
          </div>
          <div className='flex h-2.5 gap-0.5' aria-hidden>
            {parts
              .filter((part) => part.value > 0)
              .map((part) => (
                <span key={part.key} className={`${part.swatch} rounded-[4px]`} style={{ width: `${(part.value / counts.total) * 100}%` }} />
              ))}
          </div>
          <dl className='mt-0.5 flex justify-between gap-2'>
            {parts.map((part) => (
              <div key={part.key} className='flex flex-col gap-px'>
                <dt className='flex items-center gap-1.5 text-[11.5px] text-muted-foreground'>
                  <span aria-hidden className={`size-2 rounded-[2px] ${part.swatch}`} />
                  {part.label}
                </dt>
                <dd className='text-sm font-bold tabular-nums'>{part.value}</dd>
              </div>
            ))}
          </dl>
          <GrowthArea totals={overview.growth.map((point) => point.total)} />
        </>
      )}
    </HomeTile>
  )
}

/** The cumulative growth as a quiet area at the foot of the tile. */
function GrowthArea({ totals }: { totals: number[] }) {
  if (totals.length < 2) return null
  const width = 300
  const height = 44
  const min = Math.min(...totals)
  const max = Math.max(...totals)
  const span = max - min || 1
  const points = totals.map((value, index) => [
    (index * width) / (totals.length - 1),
    height - 3 - ((value - min) / span) * (height - 12),
  ])
  const line = `M${points.map(([x, y]) => `${x!.toFixed(1)} ${y!.toFixed(1)}`).join(" L")}`
  return (
    <svg aria-hidden viewBox={`0 0 ${width} ${height}`} preserveAspectRatio='none' className='mt-auto block h-11 w-full'>
      <path d={`${line} L${width} ${height} L0 ${height} Z`} className='fill-emerald-600/10 dark:fill-emerald-500/15' />
      <path d={line} fill='none' strokeWidth={2} vectorEffect='non-scaling-stroke' className='stroke-emerald-600 dark:stroke-emerald-500' />
    </svg>
  )
}
